// dashboard/patientList/PetCard.js
// Replaces PatientRow — groups all conditions for one pet. Extracted from
// App.js (2026-08-12 split).

import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { c, severityColor, severityBg, conditionLabel } from '../constants';
import { getAge, fetchTrackerData, withTimeout } from '../utils';
import { WeightMiniWidget, WellbeingMiniWidget, SeizureMiniWidget } from '../widgets/MiniWidgets';
import TrackerPopout from '../widgets/TrackerPopout';
import QuickActions from './QuickActions';
import ConditionRow from './ConditionRow';

export default function PetCard({ petGroup, onSelect, onDismissAlert, onReactivateAlert, onPrintSummary, selectedPatient, useLbs, freezeCutoff, isInCatchUpWindow, recoveryThresholds, vets, canClinical, onReassignDoctor, onAssignCondition }) {
  const { t } = useTranslation();
  const [trackersOpen,    setTrackersOpen]    = useState(false);
  const [trackerData,     setTrackerData]     = useState(null);
  const [loadingTrackers, setLoadingTrackers] = useState(false);
  const [popoutType,      setPopoutType]      = useState(null);

  const { pet, owner, conditions, severity } = petGroup;
  const color        = severityColor(severity);
  const isAnySelected = conditions.some(r => selectedPatient?.condition?.id === r.condition?.id);

  // Drop any already-fetched trackerData when freezeCutoff transitions
  // (either direction) — otherwise a card left open across a clinic's
  // payment lapsing or recovering would keep showing whatever was cached
  // at the moment it was first opened, same staleness risk as
  // PatientDetailPanel's module-level _trackerCache.
  useEffect(() => { setTrackerData(null); }, [freezeCutoff]);

  const handleTrackerToggle = async () => {
    if (!trackersOpen && !trackerData && !loadingTrackers) {
      setLoadingTrackers(true);
      try {
        const data = await withTimeout(fetchTrackerData(pet.id, freezeCutoff));
        setTrackerData(data);
      } catch (err) {
        // Non-fatal — falls through to the existing "no tracker data" empty
        // state below rather than leaving the toggle stuck on "Loading
        // trackers..." forever (previously unguarded — a stalled call, seen
        // right after a fresh login, left loadingTrackers stuck true with
        // no recovery short of a full page reload).
        console.warn('fetchTrackerData failed:', err);
      } finally {
        setLoadingTrackers(false);
      }
    }
    setTrackersOpen(o => !o);
  };

  const hasTrackers = trackerData && (trackerData.weights.length || trackerData.goodDays.length || trackerData.seizures.length);

  return (
    <div style={{
      backgroundColor: isAnySelected ? `${color}08` : c.white,
      borderRadius: 10,
      border: `1px solid ${c.border}`,
      borderLeft: `3px solid ${color}`,
      marginBottom: 8,
      overflow: 'hidden',
    }}>
      {/* Pet header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px 10px' }}>
        {/* Photo */}
        <div style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: '#e8eae6', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {pet.photo_url
            ? <img src={pet.photo_url} alt={pet.name} style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 22 }} onError={e => { e.target.style.display = 'none'; }} />
            : <span style={{ fontSize: 20 }}>🐾</span>}
        </div>
        {/* Identity */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: c.slate }}>{pet.name}</div>
          <div style={{ fontSize: 12, color: c.textSoft, marginTop: 1 }}>
            {[getAge(pet.date_of_birth), pet.breed].filter(Boolean).join(' · ')}
          </div>
          <div style={{ fontSize: 12, color: c.textSoft }}>{owner?.first_name} {owner?.last_name}</div>
        </div>
        {/* Condition pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end', maxWidth: 300 }}>
          {conditions.map(r => (
            <span key={r.condition?.id} style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
              backgroundColor: severityBg(r.severity), color: severityColor(r.severity),
            }}>
              {conditionLabel(r.condition?.condition_type)}
            </span>
          ))}
        </div>
        {/* ⋯ quick actions (print summary etc.) at pet level */}
        <QuickActions patient={conditions[0]} onSelect={onSelect} onDismissAlert={onDismissAlert} onPrintSummary={onPrintSummary} vets={vets} canClinical={canClinical} onReassignDoctor={onReassignDoctor} onAssignCondition={onAssignCondition} />
      </div>

      {/* One condition row per condition */}
      {conditions.map(row => (
        <ConditionRow
          key={row.condition?.id}
          row={row}
          onSelect={onSelect}
          onDismissAlert={onDismissAlert}
          onReactivateAlert={onReactivateAlert}
          recoveryThresholds={recoveryThresholds}
          isSelected={selectedPatient?.condition?.id === row.condition?.id}
          isInCatchUpWindow={isInCatchUpWindow}
        />
      ))}

      {/* Tracker toggle */}
      <button onClick={handleTrackerToggle} style={{
        width: '100%', background: 'none', border: 'none', borderTop: `1px solid ${c.border}`,
        padding: '7px 16px', cursor: 'pointer', textAlign: 'left',
        fontSize: 11, color: c.textSoft, display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span>{trackersOpen ? '▲' : '▼'}</span>
        <span>{loadingTrackers ? t('patientCard.loadingTrackers') : trackersOpen ? t('patientCard.hideTrackers') : t('patientCard.showTrackers')}</span>
      </button>

      {/* Tracker section */}
      {trackersOpen && (
        <div style={{ padding: '10px 16px 12px', borderTop: `1px solid ${c.border}`, backgroundColor: c.fog }}>
          {loadingTrackers ? (
            <span style={{ fontSize: 12, color: c.textSoft }}>{t('common.loading')}</span>
          ) : !hasTrackers ? (
            <span style={{ fontSize: 12, color: c.textSoft, fontStyle: 'italic' }}>{t('patientCard.noTrackerData')}</span>
          ) : (
            <>
              <WeightMiniWidget    weights={trackerData.weights}   onClick={() => setPopoutType('weight')}    useLbs={useLbs} />
              <WellbeingMiniWidget goodDays={trackerData.goodDays} onClick={() => setPopoutType('wellbeing')} />
              <SeizureMiniWidget   seizures={trackerData.seizures} onClick={() => setPopoutType('seizures')}  />
            </>
          )}
        </div>
      )}

      {popoutType && trackerData && (
        <TrackerPopout
          type={popoutType}
          data={popoutType === 'weight' ? trackerData.weights : popoutType === 'wellbeing' ? trackerData.goodDays : trackerData.seizures}
          onClose={() => setPopoutType(null)}
          useLbs={useLbs}
        />
      )}
    </div>
  );
}
